import React, { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { VAT_RATES, computeTotals, fmtEUR, vatMention } from "../../lib/invoiceConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const emptyLine = { designation: "", quantity: 1, unitPriceHT: 0, vatRate: 20 };

const QuoteForm = ({ open, onOpenChange, onCreated, clients = [], interventions = [], noVat = false }) => {
  const [buyer, setBuyer] = useState({ name: "", email: "", address: "", postalCode: "", city: "", siren: "", tvaIntra: "", isCompany: true });
  const [meta, setMeta] = useState({ clientId: "", interventionId: "", title: "", issueDate: today(), validUntil: plusDays(30), paymentTerms: "Paiement à 30 jours", notes: "" });
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [loading, setLoading] = useState(false);

  const setB = (k, v) => setBuyer((b) => ({ ...b, [k]: v }));
  const setM = (k, v) => setMeta((m) => ({ ...m, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, { ...emptyLine }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const onSelectClient = (id) => {
    setM("clientId", id);
    const c = clients.find((x) => x.id === id);
    if (c) setBuyer((b) => ({ ...b, name: c.company || c.name || b.name, email: c.email || b.email, isCompany: c.role === "agence" }));
  };

  const totals = computeTotals(lines, noVat);

  const reset = () => {
    setBuyer({ name: "", email: "", address: "", postalCode: "", city: "", siren: "", tvaIntra: "", isCompany: true });
    setMeta({ clientId: "", interventionId: "", title: "", issueDate: today(), validUntil: plusDays(30), paymentTerms: "Paiement à 30 jours", notes: "" });
    setLines([{ ...emptyLine }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!buyer.name) { toast.error("Le nom du client est obligatoire."); return; }
    if (lines.some((l) => !l.designation)) { toast.error("Chaque ligne doit avoir une désignation."); return; }
    setLoading(true);
    const payload = {
      clientId: meta.clientId || null,
      interventionId: meta.interventionId || null,
      buyer,
      title: meta.title || null,
      issueDate: meta.issueDate,
      validUntil: meta.validUntil || null,
      paymentTerms: meta.paymentTerms || null,
      notes: meta.notes || null,
      lines: lines.map((l) => ({ designation: l.designation, quantity: Number(l.quantity), unitPriceHT: Number(l.unitPriceHT), vatRate: Number(l.vatRate) })),
    };
    try {
      const { data } = await API.post("/quotes", payload);
      toast.success("Devis créé (brouillon)");
      reset();
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quote-form-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau devis</DialogTitle>
          <DialogDescription>Créé en brouillon. Le numéro est attribué à l'envoi. Lien de validation client généré automatiquement.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-3">
            <p className="font-semibold text-sm text-slate-800">Client</p>
            {clients.length > 0 && (
              <Select value={meta.clientId} onValueChange={onSelectClient}>
                <SelectTrigger data-testid="quote-client-select"><SelectValue placeholder="Pré-remplir depuis un client existant (optionnel)" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `— ${c.company}` : ""} ({c.role})</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nom / Raison sociale *</Label>
                <Input value={buyer.name} onChange={(e) => setB("name", e.target.value)} required data-testid="quote-buyer-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email (pour l'envoi)</Label>
                <Input type="email" value={buyer.email} onChange={(e) => setB("email", e.target.value)} data-testid="quote-buyer-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Adresse</Label>
                <Input value={buyer.address} onChange={(e) => setB("address", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label className="text-xs">CP</Label><Input value={buyer.postalCode} onChange={(e) => setB("postalCode", e.target.value)} /></div>
                <div className="space-y-1"><Label className="text-xs">Ville</Label><Input value={buyer.city} onChange={(e) => setB("city", e.target.value)} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">SIREN (B2B)</Label><Input value={buyer.siren} onChange={(e) => setB("siren", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">N° TVA</Label><Input value={buyer.tvaIntra} onChange={(e) => setB("tvaIntra", e.target.value)} /></div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Objet du devis</Label>
              <Input value={meta.title} onChange={(e) => setM("title", e.target.value)} placeholder="Ex : Remplacement vitrage appartement" data-testid="quote-title" />
            </div>
            {interventions.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Dossier intervention (optionnel)</Label>
                <Select value={meta.interventionId} onValueChange={(v) => setM("interventionId", v)}>
                  <SelectTrigger data-testid="quote-intervention-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    {interventions.map((i) => (<SelectItem key={i.id} value={i.id}>{i.dossierNumber} — {i.clientName || ""}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1"><Label className="text-xs">Date du devis</Label><Input type="date" value={meta.issueDate} onChange={(e) => setM("issueDate", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Valable jusqu'au</Label><Input type="date" value={meta.validUntil} onChange={(e) => setM("validUntil", e.target.value)} data-testid="quote-valid-until" /></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm text-slate-800">Lignes de prestations</p>
              <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="quote-add-line"><Plus className="w-4 h-4 mr-1" /> Ligne</Button>
            </div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`quote-line-${i}`}>
                <div className="col-span-12 sm:col-span-5 space-y-1">
                  {i === 0 && <Label className="text-xs">Désignation</Label>}
                  <Input value={l.designation} onChange={(e) => setLine(i, "designation", e.target.value)} data-testid={`quote-line-designation-${i}`} />
                </div>
                <div className="col-span-3 sm:col-span-2 space-y-1">{i === 0 && <Label className="text-xs">Qté</Label>}<Input type="number" min="0" step="0.01" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`quote-line-quantity-${i}`} /></div>
                <div className="col-span-4 sm:col-span-2 space-y-1">{i === 0 && <Label className="text-xs">PU HT</Label>}<Input type="number" min="0" step="0.01" value={l.unitPriceHT} onChange={(e) => setLine(i, "unitPriceHT", e.target.value)} data-testid={`quote-line-price-${i}`} /></div>
                {!noVat && (
                  <div className="col-span-3 sm:col-span-2 space-y-1">
                    {i === 0 && <Label className="text-xs">TVA %</Label>}
                    <Select value={String(l.vatRate)} onValueChange={(v) => setLine(i, "vatRate", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{VAT_RATES.map((r) => (<SelectItem key={r} value={String(r)}>{r}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2 sm:col-span-1"><Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></div>
              </div>
            ))}
          </div>

          <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1 ml-auto sm:w-72">
            <div className="flex justify-between"><span>Total HT</span><span>{fmtEUR(totals.totalHT)}</span></div>
            {!noVat && totals.breakdown.map((b) => (<div key={b.rate} className="flex justify-between text-muted-foreground text-xs"><span>TVA {b.rate}%</span><span>{fmtEUR(b.vatAmount)}</span></div>))}
            <div className="flex justify-between font-bold text-[hsl(207,78%,42%)] border-t pt-1"><span>{noVat ? "Net à payer" : "Total TTC"}</span><span data-testid="quote-total-ttc">{fmtEUR(noVat ? totals.totalHT : totals.totalTTC)}</span></div>
            {noVat && <p className="text-[11px] text-muted-foreground pt-1">{vatMention("franchise")}</p>}
          </div>

          <div className="space-y-1"><Label className="text-xs">Notes / conditions</Label><Textarea rows={2} value={meta.notes} onChange={(e) => setM("notes", e.target.value)} /></div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="quote-form-submit">{loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer le devis</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteForm;
